import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { adminApi, getAdminToken, setAdminToken } from "../lib/adminApi";
import { isSupabaseConfigured } from "../lib/supabase";
import { hasPermission } from "../lib/permissions";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getAdminToken();
    if (!token || !isSupabaseConfigured) {
      setLoading(false);
      return;
    }
    adminApi
      .me(token)
      .then((data) => setAdmin(data))
      .catch(() => {
        setAdminToken(null);
        setAdmin(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo(
    () => ({
      admin,
      loading,
      isSuperadmin: admin?.role === "superadmin",
      configured: isSupabaseConfigured,
      can: (feature, action = "view") => hasPermission(admin, feature, action),
      login: async (username, password) => {
        const data = await adminApi.login(username, password);
        setAdminToken(data.token);
        setAdmin(data.admin);
        return data.admin;
      },
      logout: async () => {
        const token = getAdminToken();
        try {
          if (token) await adminApi.logout(token);
        } catch {
          // still clear the local session
        }
        setAdminToken(null);
        setAdmin(null);
      },
    }),
    [admin, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
